import React, { useEffect, useMemo, useRef, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  EmailOutlined,
  ExpandMore,
  TimerOffTwoTone,
  Warning,
} from "@mui/icons-material";
import { Button, DialogActions, ListItem, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import { caseActivityActions } from "@_actions/caseActivity.actions";
import { caseActions } from "@_actions/case.actions";
import { caseConstants } from "@_constants/case.constants";
import { emailConstants } from "@_constants/email_flows.constants";
import {
  createNameFromMimeAndChecksum,
  getUserDataFromJwt,
  sanitizeHtml,
  shortnString,
} from "@_utils/helpers";
import { customerService } from "@_services/customer.service";
import { templateService } from "@_services/template.service";

import AreYouSurePopup from "@_components/popups/AreYouSurePopup/AreYouSurePopup";
import ChangeOpponentContactDetailsPopup from "@_components/popups/ChangeOpponentContactDetailsPopup.js/ChangeOpponentContactDetailsPopup";
import DialogPopup from "@_components/DialogPopup/DialogPopup";
import InfoPopup from "@_components/popups/InfoPopup/InfoPopup";
import MailPopup from "@_components/popups/MailPopup/MailPopup";
import SelectPopup from "@_components/popups/SelectEmailTemplatePopup/SelectPopup";

import { useActivityExpand } from "../../hooks/useActivityExpand";
import { useConfirmPopup } from "@_hooks/useConfirmPopup";
import EmailToolbar from "./EmailToolBar";
import CardShell from "../../core/CardShell";
import styles from "../../CaseActivityCard.module.css";

// ── Helpers ───────────────────────────────────────────────────────────────────

function getEmailAddress(field) {
  if (!field) return "";
  return Array.isArray(field) ? field[0].address : field;
}

function getEmailAddressString(field) {
  if (!field) return "";
  return Array.isArray(field) ? field.map((x) => x.address).join(", ") : field;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function EmailIcon({ activity, unreadUpdated }) {
  const { content } = activity;
  return (
    <>
      <EmailOutlined />
      {content.status === caseConstants.CASE_ACTIVITY_STATE_FAILED && (
        <Tooltip title="Failed to send Email">
          <Warning className={styles.warning_icon} />
        </Tooltip>
      )}
      {content.status === caseConstants.CASE_ACTIVITY_STATE_PAUSED && (
        <Tooltip title="Automation paused">
          <TimerOffTwoTone data-testid="automation-paused-icon" />
        </Tooltip>
      )}
      {content?.unread === 1 && !unreadUpdated && (
        <div className={styles.unread_marker} data-testid="unread-marker" />
      )}
    </>
  );
}

function EmailContentPreview({ activity, caseData, detailsOpen }) {
  const { content, type } = activity;

  if (type === caseConstants.CASEACTIVITY_TYPE_EMAIL_SCHEDULED) {
    return (
      <>
        <div className={styles.content_email_meta}>
          {caseData?.opponent_contact_details?.email ?? ""}
        </div>
        <div
          className={styles.content_subject_ellipsis}
          title={content.template_subject}
        >
          {content.mail_content
            ? content.mail_content.subject
            : content.template_subject}
        </div>
      </>
    );
  }

  const addressField = content.to || content.from;

  return (
    <>
      <div className={styles.content_email_meta}>
        {getEmailAddress(addressField)}
      </div>
      <div
        className={
          detailsOpen
            ? styles.content_subject_expanded
            : styles.content_subject_ellipsis
        }
        title={content.subject}
      >
        {content.subject}
      </div>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * EmailActivityCard
 *
 * Handles EMAIL_IN, EMAIL_OUT, and EMAIL_SCHEDULED activity types.
 *
 * Responsibilities:
 * - Lazy-loads full email body on expand
 * - Renders email toolbar (Reply, Reply with template, Reply All, Mark Unread, Attachments)
 * - Handles scheduled-email delete with failed-count sync
 * - Opens MailPopup for resend / reply with template flows
 * - Opens ChangeOpponentContactDetailsPopup when sender differs from stored contact
 */
export default function EmailActivityCard({
  activity,
  caseData,
  resumeAutomation,
  emailTemplates,
}) {
  const dispatch = useDispatch();
  const jwtData = getUserDataFromJwt();

  const caseActivityReducer = useSelector((s) => s.caseActivityReducer);
  const caseReducer = useSelector((s) => s.caseReducer);
  const emailSetup = useSelector(
    (s) => s.emailSetupReducer.email_setup.active_email_setup,
  );

  const { detailsOpen, animateExpand, toggle } = useActivityExpand();
  const confirm = useConfirmPopup();

  const [unreadUpdated, setUnreadUpdated] = useState(false);
  const [mailLoaded, setMailLoaded] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [isReplyPopupOpen, setIsReplyPopupOpen] = useState(false);
  const [emailData, setEmailData] = useState({});
  const [openMailPopup, setOpenMailPopup] = useState(false);
  const [openSelectTemplate, setOpenSelectTemplate] = useState(false);
  const [openInfoPopup, setOpenInfoPopup] = useState(false);
  const [openChangeContactPopup, setOpenChangeContactPopup] = useState(false);

  const replyToAll = useRef(false);

  const isScheduled =
    activity.type === caseConstants.CASEACTIVITY_TYPE_EMAIL_SCHEDULED;
  const isEmailIn = activity.type === caseConstants.CASEACTIVITY_TYPE_EMAIL_IN;
  const isEmailOut =
    activity.type === caseConstants.CASEACTIVITY_TYPE_EMAIL_OUT;

  // ── Effects ────────────────────────────────────────────────────────────────

  /**
   * Lazy-load email body when the card is expanded (EMAIL_IN / EMAIL_OUT only).
   *
   * Only for email activities (in/out). Sets mailLoaded flag BEFORE dispatching
   * to prevent duplicate fetches on re-render.
   * @dependencies
   * - detailsModalOpen, activity.type, activity.content, activity.case_id, mailLoaded, dispatch
   *
   * @triggers
   * When modal opens and content is missing, or related activity fields change while open.
   */

  useEffect(() => {
    if (
      detailsOpen &&
      (isEmailIn || isEmailOut) &&
      activity.content &&
      !activity.content.mail_content &&
      !mailLoaded
    ) {
      setMailLoaded(true);
      dispatch(
        caseActivityActions.getEmailContent(
          activity.content.email_id,
          activity.case_id,
          activity.content.unread > 0,
        ),
      );
    }
  }, [
    detailsOpen,
    activity.content,
    activity.case_id,
    mailLoaded,
    dispatch,
    isEmailIn,
    isEmailOut,
  ]);

  /** Reset mailLoaded flag when card collapses so next open re-fetches.
   *
   * Pairs with the lazy-load effect above.
   *
   * @dependencies
   * - detailsModalOpen
   *
   * @triggers
   * When modal closes.
   */
  useEffect(() => {
    if (!detailsOpen) setMailLoaded(false);
  }, [detailsOpen]);

  /**
   * For scheduled-email activities, Prefetches scheduled email template content to show preview in the row.
   *
   * When activity type changes to scheduled email or content updates,
   * fetch template details (subject, body, config) so user can see
   * what will be sent without opening modal.
   */
  useEffect(() => {
    setMailLoaded(false);
    if (isScheduled && activity.content && !activity.content.mail_content) {
      dispatch(
        caseActivityActions.getEmailTemplateContentByTemplate(
          activity.case_id,
          activity.content.source_email_template_id,
          {
            subject: activity.content.template_subject,
            content: activity.content.template_content,
            config: activity.content.template_config,
            id: activity.content.source_email_template_id,
          },
        ),
      );
      setMailLoaded(true);
    }
  }, [
    activity.content,
    activity.case_id,
    activity.type,
    dispatch,
    isScheduled,
  ]);

  /**
   * After "mark unread" succeeds, clear Redux status only.
   * Keep mail_content and mailLoaded — nothing in the email body changed.
   */
  useEffect(() => {
    if (caseActivityReducer.status.EMAIL_UNREAD === "success") {
      dispatch(caseActivityActions.clearState("EMAIL_UNREAD"));
    }
  }, [caseActivityReducer.status.EMAIL_UNREAD, dispatch]);

  /**
   * Close reply/render popups after a successful send.
   *
   * @dependencies
   * - caseReducer.status.CASE_SEND_LETTER, CASE_SEND_EMAIL
   *
   * @triggers
   * When either send status becomes "success".
   *
   * @user_flow
   * 1. User sends an email or letter from this card’s popup.
   * 2. On success: close render popup and reply popup.
   *
   */
  useEffect(() => {
    if (
      caseReducer.status.CASE_SEND_LETTER === "success" ||
      caseReducer.status.CASE_SEND_EMAIL === "success"
    ) {
      setOpenMailPopup(false);
      setIsReplyPopupOpen(false);
    }
  }, [caseReducer.status.CASE_SEND_LETTER, caseReducer.status.CASE_SEND_EMAIL]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleToggle = () => {
    const expanded = toggle();
    if (expanded && isEmailIn) {
      setUnreadUpdated(true);
      checkContactDetails();
    }
  };

  const checkContactDetails = () => {
    if (
      isEmailIn &&
      activity.content?.unread === 1 &&
      activity.content.from?.[0]?.address !==
        caseData.opponent_contact_details?.email
    ) {
      setOpenChangeContactPopup(true);
    }
  };

  const handleMarkUnread = () => {
    dispatch(
      caseActivityActions.markUnread(
        activity.content.email_id,
        activity.case_id,
      ),
    );
    setUnreadUpdated(false);
  };

  const handleDeleteScheduled = (e) => {
    e.stopPropagation();
    confirm.prompt("Are you sure you want to delete this email?", () => {
      confirm.close();
      const isFailed =
        activity.content.status === caseConstants.CASE_ACTIVITY_STATE_FAILED;
      dispatch(
        caseActivityActions.deleteScheduledMails(
          activity.case_id,
          activity.id,
          isFailed
            ? (d) =>
                d(
                  caseActions.decreaseFailed(
                    activity.case_id,
                    activity.content.type ===
                      caseConstants.CASEACTIVITY_TYPE_TRIGGERED
                      ? "num_triggered_emails_failed"
                      : "num_scheduled_emails_failed",
                  ),
                )
            : undefined,
        ),
      );
    });
  };

  const selectEmailTemplate = (templateId) => {
    templateService.getCaseTemplate(caseData.id, templateId).then(
      (template) => {
        setEmailData({
          content: template.content,
          name: template.name,
          subject: template.subject,
        });
        setOpenSelectTemplate(false);
        setIsReplyPopupOpen(true);
      },
      (error) => alert(error),
    );
  };

  const handleOpenTemplatePopup = (e) => {
    e.stopPropagation();
    setEmailData({
      content: activity.content.mail_content?.content,
      name: activity.content.template_name,
      subject: activity.content.mail_content?.subject,
      opponentEmail: caseData?.opponent_contact_details?.email,
    });
    setOpenMailPopup(true);
  };

  const handleReplyWithTemplate = () => {
    const customTemplates = emailTemplates?.filter(
      (t) => t.type === emailConstants.TEMPLATE_TYPE_CUSTOM,
    );
    if (customTemplates?.length > 0) {
      replyToAll.current = false;
      setOpenSelectTemplate(true);
    } else {
      setOpenInfoPopup(true);
    }
  };

  // ── Attachment helpers ─────────────────────────────────────────────────────

  const numAttachments =
    activity?.content?.mail_content?.data?.attachments?.length ?? 0;

  const downloadAttachment = (attachment) => {
    customerService
      .downloadAttachment(activity.content.email_id, attachment.checksum)
      .then((response) => {
        if (!response || response.status !== 200) {
          alert("Can not download. Unauthorized");
          return;
        }
        return response.blob();
      })
      .then((blob) => {
        if (!blob) return;
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", attachment.filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      })
      .catch(() => alert("Can not download. Unauthorized"));
  };

  const renderAttachments = () => {
    const attachments = activity?.content?.mail_content?.data?.attachments;
    if (!attachments) return <>This mail has no attachments</>;

    return attachments.map((item) => (
      <ListItem key={item.checksum}>
        <div className={styles.attachement_row}>
          <div>
            <table>
              <tbody>
                <tr>
                  <td>
                    {item.filename ||
                      createNameFromMimeAndChecksum(
                        item.checksum,
                        item.contentType,
                      )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <Button
            className={styles.btn_cancel}
            onClick={() => downloadAttachment(item)}
          >
            Download
          </Button>
        </div>
      </ListItem>
    ));
  };

  // ── Safe HTML wrapper ──────────────────────────────────────────────────────

  const returnSafeHtml = useMemo(
    () => (html) => {
      const sanitized = sanitizeHtml(html);
      return `
      <!doctype html>
      <html>
      <head>
      <base target="_blank" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500&display=swap" />
        <style>
        body {
          font-family: "Roboto";
          color: #4b4b4b;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;}
          p {
          font-family: "Roboto";
          line-height: 1.6;
          margin-left: 10px;
          font-size:18px;
        }
        </style>
      </head>
      <body>
      ${sanitized}
      </body>
      </html>
    `;
    },
    [],
  );

  // ── Expanded email body ────────────────────────────────────────────────────

  const renderEmailBody = () => {
    const { content } = activity;

    /** Scheduled emails render from template content, others from mail_content. */
    if (isScheduled) {
      if (!content.template_content) {
        return (
          <div className={styles.mail_status_message}>
            {caseActivityReducer.status.EMAIL_CONTENT === "failure"
              ? "Failed to load mail..."
              : "loading..."}
          </div>
        );
      }
      return (
        <div className={styles.notePopupTextContainer}>
          {/* Resend banner for failed scheduled emails */}
          {content.status === caseConstants.CASE_ACTIVITY_STATE_FAILED && (
            <div className={styles.email_resend_container}>
              <p className={styles.resend_text}>
                Sending email failed:{" "}
                {content.history?.find(
                  (a) => a.event === caseConstants.CASE_ACTIVITY_STATE_FAILED,
                )?.error_msg ?? ""}
              </p>
              <Button
                className={styles.btn_default}
                onClick={handleOpenTemplatePopup}
              >
                Resend
              </Button>
            </div>
          )}
          {content.mail_content && (
            <iframe
              className={styles.email_iframe}
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              csp="script-src 'none'"
              srcDoc={returnSafeHtml(content.mail_content.content)}
              title="Email Content"
            />
          )}
        </div>
      );
    }

    // EMAIL_IN / EMAIL_OUT
    if (!content.mail_content) {
      return (
        <div className={styles.mail_status_message}>
          {caseActivityReducer.status.EMAIL_CONTENT === "failure"
            ? "Failed to load mail..."
            : "loading..."}
        </div>
      );
    }

    const rawHtml = content.mail_content.data.html;
    const htmlSrc = returnSafeHtml(
      typeof rawHtml === "string"
        ? rawHtml
        : content.mail_content.data.textAsHtml,
    );

    return (
      <div className={styles.notePopupTextContainer}>
        <EmailToolbar
          isEmailIn={isEmailIn}
          isEmailOut={isEmailOut}
          activity={activity}
          replyToAll={replyToAll}
          onSetIsReplyPopupOpen={setIsReplyPopupOpen}
          onHandleReplyWithTemplate={handleReplyWithTemplate}
          onHandleMarkUnread={handleMarkUnread}
          numAttachments={numAttachments}
          onSetShowAttachments={setShowAttachments}
        />
        <iframe
          className={styles.email_iframe}
          csp="script-src 'none'"
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          srcDoc={htmlSrc}
          title="Email Content"
        />
      </div>
    );
  };

  // ── Type text / subtext helpers ────────────────────────────────────────────

  const typeText = isScheduled
    ? activity.content.type === caseConstants.CASEACTIVITY_TYPE_TRIGGERED
      ? "Triggered Email"
      : "Scheduled Email"
    : isEmailOut
      ? "Email sent"
      : "Email received";

  const typeSubText = isScheduled ? (
    <div className={styles.type_subtext_scheduled_template}>
      {activity.content.template_name}
    </div>
  ) : isEmailOut &&
    (activity.content?.automation_info?.type ===
      caseConstants.CASEACTIVITY_TYPE_SCHEDULED ||
      (activity.content?.automation_info?.type ===
        caseConstants.CASEACTIVITY_TYPE_TRIGGERED &&
        activity.content?.automation_info?.trigger_event ===
          emailConstants.TRIGGER_EVENT_DISOUNT_END)) ? (
    "automated"
  ) : (
    activity.user_name
  );

  // ── Opener column ──────────────────────────────────────────────────────────

  const openerColumn = (
    <>
      {isScheduled && (
        <DeleteIcon
          data-testid="scheduled-email-delete-icon"
          className={styles.delete_shopping_icon}
          onClick={handleDeleteScheduled}
        />
      )}
      <div className={styles.opener_expand_wrap} onClick={handleToggle}>
        <ExpandMore
          className={`${styles.opener_expand_more} ${detailsOpen ? styles.gray : ""}`}
        />
      </div>
    </>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <CardShell
        activity={activity}
        animateExpand={animateExpand}
        icon={<EmailIcon activity={activity} unreadUpdated={unreadUpdated} />}
        typeText={typeText}
        typeSubText={typeSubText}
        contentPreview={
          <EmailContentPreview
            activity={activity}
            caseData={caseData}
            detailsOpen={detailsOpen}
          />
        }
        openerColumn={openerColumn}
        onContentClick={handleToggle}
        hasOpener
      >
        {detailsOpen && (
          <div className={styles.mailContainer}>{renderEmailBody()}</div>
        )}
      </CardShell>

      {/* ── Reply popup (EMAIL_IN / EMAIL_OUT) ── */}
      <MailPopup
        title={
          isEmailOut
            ? "Reply to " + getEmailAddressString(activity.content.to)
            : "Reply to " + getEmailAddressString(activity.content.from)
        }
        open={isReplyPopupOpen}
        email={activity.content.mail_content}
        close={() => {
          setIsReplyPopupOpen(false);
          setEmailData({});
        }}
        data={emailData}
        caseId={activity.case_id}
        emailSetup={emailSetup}
        resumeAutomation={resumeAutomation}
        isReplyMode
        isSelfReply={isEmailOut}
        replyToAll={replyToAll.current && !isEmailOut}
        JwtData={jwtData}
      />

      {/* ── Resend / send preview popup ── */}
      <MailPopup
        title={
          emailData.name === emailConstants.NEW_MAIL
            ? "New mail"
            : `mail preview: ${emailData.name}`
        }
        open={openMailPopup}
        close={() => {
          setOpenMailPopup(false);
        }}
        data={emailData}
        caseId={activity.case_id}
        emailSetup={emailSetup}
        activity={activity}
        isReplyMode={false}
        resumeAutomation={resumeAutomation}
        JwtData={jwtData}
      />

      {/* ── Attachments dialog ── */}
      <DialogPopup
        open={showAttachments}
        onClose={() => setShowAttachments(false)}
        title={`Attachments of: "${shortnString(activity.content.subject, 50)}"`}
      >
        <div className={styles.dialogContent}>{renderAttachments()}</div>
        <DialogActions className={styles.dialog_actions_compact}>
          <Button
            onClick={() => setShowAttachments(false)}
            className={styles.btn_cancel}
          >
            Close
          </Button>
        </DialogActions>
      </DialogPopup>

      {/* ── Select template popup ── */}
      <SelectPopup
        open={openSelectTemplate}
        close={() => setOpenSelectTemplate(false)}
        selections={emailTemplates?.filter(
          (t) => t.type === emailConstants.TEMPLATE_TYPE_CUSTOM,
        )}
        select={selectEmailTemplate}
        title="Select email template"
      />

      {/* ── Info popup (no custom templates) ── */}
      <InfoPopup
        open={openInfoPopup}
        title="NOTICE"
        close={() => setOpenInfoPopup(false)}
      >
        <p>
          This case's email flow does not contain any custom email template.
          <br />
          Here is how you can create one:
          <ul>
            <li>
              Go to{" "}
              <span className={styles.info_popup_emphasis}>
                {"Templates > Email Flows > "}
                {caseData?.email_flow?.name}
              </span>
            </li>
            <li>Click on 'ADD CUSTOM EMAIL'</li>
          </ul>
        </p>
      </InfoPopup>

      {/* ── Change opponent contact popup ── */}
      <ChangeOpponentContactDetailsPopup
        open={openChangeContactPopup}
        close={() => setOpenChangeContactPopup(false)}
        from={activity?.content?.from ?? ""}
        caseData={caseData}
        opponentContactDetails={caseData.opponent_contact_details}
      />

      {/* ── Delete confirmation ── */}
      <AreYouSurePopup
        open={confirm.open}
        close={confirm.close}
        onClick={confirm.action}
      >
        <p className={styles.are_you_sure_preline}>{confirm.text}</p>
      </AreYouSurePopup>
    </>
  );
}
